import debounce from 'lodash/debounce';
import type { Dispatch } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { QUESTIONNAIRE_QUESTIONS } from '@constants/constants';
import type { YesNoBoolean, YesNoBooleanFormValue } from '@customTypes/form';
import { initialState } from '@store/store';

import { OrderActions, type OrderActionType } from '../types/Order.actions';
import type { SubjectPayload } from '../types/Order.types';
import type { FormType } from '../types/Wizard.types';
import { loadWizardState, saveWizardState } from '../utils/orderStorage';

import type { FamilyHistory } from '@customTypes';

interface UseWizardFormProps {
  subject?: SubjectPayload;
  family_history: FamilyHistory;
  dispatch: Dispatch<OrderActionType>;
  promoCode: string | null;
  // Minor orders pre-check the parent/guardian consent by default.
  isMinorOrder?: boolean;
  // HIE is pre-checked when shown; leave unchecked when the campaign omits it.
  includeHIEConsent: boolean;
}

interface UseWizardFormReturn {
  values: FormType;
  updateValue: (path: string, val: unknown) => void;
  questionTexts: Record<string, string>;
  flushStorage: () => void;
}

/**
 * Hook that manages wizard form state, updates, and prefill logic.
 * Handles path-based updates and automatic context synchronization.
 */
export function useWizardForm({
  subject,
  family_history,
  dispatch,
  promoCode,
  isMinorOrder = false,
  includeHIEConsent,
}: UseWizardFormProps): UseWizardFormReturn {
  // Load saved wizard state for initialization
  const savedWizardState = loadWizardState();
  const defaultConsents: FormType['consents'] = {
    acceptGeneral: false,
    acceptDNAVisit: false,
    familyHistoryAttest: false,
    acceptHIE: includeHIEConsent,
    acceptResearchContact: false,
    acceptTransactionalSms: true,
    acceptMarketing: false,
    acceptMinor: isMinorOrder,
  };

  const [values, setValues] = useState<FormType>({
    sexAtBirth: '',
    phone: '',
    address: { street: '', street2: '', city: '', state: '', zip: '' },
    consent: { accepted: false },
    questionnaire: Object.keys(QUESTIONNAIRE_QUESTIONS).reduce(
      (acc, key) => {
        acc[key] = '' as const;
        return acc;
      },
      {} as Record<string, ''>,
    ),
    consents: { ...defaultConsents, ...savedWizardState?.consents },
    paymentIntentId: undefined, // Never restore paymentIntentId for security
    promoCode: promoCode || savedWizardState?.promoCode || '',
    useShippingForBilling: savedWizardState?.useShippingForBilling ?? true, // Default to using shipping address
    billingAddress: savedWizardState?.billingAddress,
    // Only restore if payment was previously confirmed
    confirmedBillingAddress: savedWizardState?.confirmedBillingAddress,
  });

  const questionTexts = useMemo(() => {
    try {
      const state = initialState as {
        testRequestInfo?: { family_history?: Record<string, { question_text?: string }> };
      };
      const fh = state.testRequestInfo?.family_history || {};
      const map: Record<string, string> = {};
      Object.keys(QUESTIONNAIRE_QUESTIONS).forEach(k => {
        map[k] = fh[k]?.question_text || k;
      });
      return map;
    } catch {
      return {} as Record<string, string>;
    }
  }, []);

  // Prefill from context subject info (only once)
  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (didPrefillRef.current) return;
    setValues((prev: FormType) => ({
      ...prev,
      sexAtBirth: (subject?.demographics?.sex as string | undefined) || prev.sexAtBirth,
      phone: subject?.contact_info?.[0]?.phone || prev.phone,
      address: {
        street: subject?.contact_info?.[0]?.address?.street_address || prev.address?.street || '',
        street2: subject?.contact_info?.[0]?.address?.street_address_2 || prev.address?.street2 || '',
        city: subject?.contact_info?.[0]?.address?.city || prev.address?.city || '',
        state: subject?.contact_info?.[0]?.address?.region || prev.address?.state || '',
        zip: subject?.contact_info?.[0]?.address?.postal_code || prev.address?.zip || '',
      },
      questionnaire: Object.keys(QUESTIONNAIRE_QUESTIONS).reduce<Record<string, YesNoBooleanFormValue>>((acc, key) => {
        const resp = family_history && family_history[key]?.response ? family_history[key]?.response : '';
        acc[key] = resp || '';
        return acc;
      }, {}),
    }));
    didPrefillRef.current = true;
    // We want to run this on first subject prefill only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  // Refs to track pending side effects (dispatch and storage)
  const pendingDispatchRef = useRef<Array<() => void>>([]);
  const latestValuesRef = useRef<FormType>(values);
  const needsStorageSaveRef = useRef(false);

  // Update ref whenever values change
  useEffect(() => {
    latestValuesRef.current = values;
  }, [values]);

  // Save wizard-specific fields to storage whenever they change
  const saveWizardFields = (updatedValues: FormType) => {
    const savedState = loadWizardState();
    saveWizardState({
      ...savedState,
      billingAddress: updatedValues.billingAddress,
      consents: updatedValues.consents,
      useShippingForBilling: updatedValues.useShippingForBilling ?? true,
      promoCode: updatedValues.promoCode,
      confirmedBillingAddress: updatedValues.confirmedBillingAddress,
      // Preserve currentStep if it exists
      currentStep: savedState?.currentStep ?? 0,
    });
  };

  // Create debounced save function (300ms delay)
  const debouncedSaveWizardFields = useMemo(
    () => debounce((updatedValues: FormType) => saveWizardFields(updatedValues), 300),
    [],
  );

  // Flush function to immediately save (for step change, submission, unmount)
  const flushStorage = useMemo(
    () => () => {
      debouncedSaveWizardFields.flush();
    },
    [debouncedSaveWizardFields],
  );

  // Process pending dispatch actions synchronously after render (before paint)
  useLayoutEffect(() => {
    if (pendingDispatchRef.current.length > 0) {
      const actions = pendingDispatchRef.current;
      pendingDispatchRef.current = [];
      actions.forEach(action => action());
    }
  });

  // Process storage saves after render (debounced)
  useEffect(() => {
    if (needsStorageSaveRef.current) {
      needsStorageSaveRef.current = false;
      debouncedSaveWizardFields(latestValuesRef.current);
    }
  });

  // Flush storage on unmount
  useEffect(() => {
    return () => {
      debouncedSaveWizardFields.flush();
      debouncedSaveWizardFields.cancel();
    };
  }, [debouncedSaveWizardFields]);

  const updateValue = (path: string, val: unknown) => {
    setValues((prev: FormType) => {
      const [field, key] = path.split('.');

      switch (field) {
        case 'address': {
          if (!key) return prev;
          const addressKey = key as keyof NonNullable<FormType['address']>;
          const nextAddressUI = { ...(prev.address || {}), [addressKey]: val as string };
          // Persist address edits using the computed next address to avoid stale overwrites
          const addrPayload = {
            street_address: nextAddressUI.street || undefined,
            street_address_2: nextAddressUI.street2 || undefined,
            city: nextAddressUI.city || undefined,
            region: nextAddressUI.state || undefined,
            postal_code: nextAddressUI.zip || undefined,
          };
          // Schedule dispatch after render to avoid updating parent component during render
          // Capture current subject to avoid stale closure
          const currentSubject = subject;
          pendingDispatchRef.current.push(() => {
            const existingCI0 = (currentSubject?.contact_info && currentSubject.contact_info[0]) || {};
            dispatch({
              type: OrderActions.MERGE_SUBJECT,
              value: { contact_info: [{ ...existingCI0, address: addrPayload }] },
            });
          });
          return { ...prev, address: nextAddressUI };
        }

        case 'billingAddress': {
          if (!key) return prev;
          const billingKey = key as keyof NonNullable<FormType['billingAddress']>;
          const next = {
            ...prev,
            billingAddress: { ...(prev.billingAddress || {}), [billingKey]: val as string },
          };
          // Mark for storage save after render
          needsStorageSaveRef.current = true;
          return next;
        }

        case 'consents': {
          if (!key) return prev;
          const consentKey = key as keyof NonNullable<FormType['consents']>;
          const next = {
            ...prev,
            consents: { ...(prev.consents || {}), [consentKey]: Boolean(val) },
          };
          // Mark for storage save after render
          needsStorageSaveRef.current = true;
          return next;
        }

        case 'questionnaire': {
          if (!key) return prev;

          const questionKey = key;
          const previousQuestionnaire = prev.questionnaire || {};
          const response = val as YesNoBoolean;

          // Persist selection to context family_history with question_text
          // Schedule dispatch after render to avoid updating parent component during render
          // Capture questionTexts to avoid stale closure
          const currentQuestionTexts = questionTexts;

          pendingDispatchRef.current.push(() => {
            dispatch({
              type: OrderActions.MERGE_FAMILY_HISTORY,
              value: {
                [questionKey]: {
                  question_text: currentQuestionTexts[questionKey] || questionKey,
                  response,
                },
              },
            });
          });

          return {
            ...prev,
            questionnaire: {
              ...previousQuestionnaire,
              [questionKey]: response,
            },
          };
        }

        case 'phone': {
          const nextPhone = val as string;
          // Schedule dispatch after render to avoid updating parent component during render
          // Capture current subject to avoid stale closure
          const currentSubject = subject;
          pendingDispatchRef.current.push(() => {
            const existingCI0 = (currentSubject?.contact_info && currentSubject.contact_info[0]) || {};
            dispatch({
              type: OrderActions.MERGE_SUBJECT,
              value: { contact_info: [{ ...existingCI0, phone: nextPhone || undefined }] },
            });
          });
          return { ...prev, phone: nextPhone };
        }

        case 'sexAtBirth': {
          const nextSex = val as string;
          // Schedule dispatch after render to avoid updating parent component during render
          // Capture current subject to avoid stale closure
          const currentSubject = subject;
          pendingDispatchRef.current.push(() => {
            dispatch({
              type: OrderActions.MERGE_SUBJECT,
              value: { demographics: { ...(currentSubject?.demographics || {}), sex: nextSex || undefined } },
            });
          });
          return { ...prev, sexAtBirth: nextSex };
        }

        case 'useShippingForBilling': {
          const next = { ...prev, useShippingForBilling: Boolean(val) };
          // Mark for storage save after render
          needsStorageSaveRef.current = true;
          return next;
        }

        case 'paymentIntentId':
          // paymentIntentId is intentionally not persisted for security
          return { ...prev, paymentIntentId: val as string };

        case 'promoCode': {
          const next = { ...prev, promoCode: val as string };
          // Mark for storage save after render
          needsStorageSaveRef.current = true;
          return next;
        }

        case 'confirmedBillingAddress': {
          const next = { ...prev, confirmedBillingAddress: val as FormType['confirmedBillingAddress'] };
          // Mark for storage save after render
          needsStorageSaveRef.current = true;
          return next;
        }

        default:
          return prev;
      }
    });
  };

  return {
    values,
    updateValue,
    questionTexts,
    flushStorage,
  };
}
